import { IMyRaffleRewardClaimListRewardDTO } from "@hooks/dtos/raffles/IRaffleRewardClaimDTO";
import { Masks } from "@hooks/functions/Masks/Masks";
import dayjs from "dayjs";
import {
  FaComments,
  FaFileArrowUp,
  FaPaperPlane,
  FaUpload,
} from "react-icons/fa6";
import {
  RewardClaimDeliveryTypeBadge,
  RewardClaimStatusBadge,
} from "../../components/RewardClaimBadge";
import { RewardThumb } from "../../components/RewardThumb";
import {
  IRewardClaimDetailFile,
  IRewardClaimDetailRequirement,
} from "../mock";

const masks = new Masks();

interface IProps {
  rewards: IMyRaffleRewardClaimListRewardDTO[];
  files: IRewardClaimDetailFile[];
  requirements: IRewardClaimDetailRequirement[];
}

function getRequirement(
  reward: IMyRaffleRewardClaimListRewardDTO,
  requirements: IRewardClaimDetailRequirement[],
) {
  return requirements.find((item) => item.claimId === reward.claimId);
}

export function RewardDetailRewards({ rewards, files, requirements }: IProps) {
  return (
    <div className="card h-100">
      <div className="card-header">
        <div className="card-title">Prêmios da Campanha</div>
        <div className="card-category">
          Cada prêmio possui solicitação e documentos próprios.
        </div>
      </div>
      <div className="card-body">
        <div className="d-grid gap-3">
          {rewards.map((reward) => {
            const requirement = getRequirement(reward, requirements);
            const rewardFiles = files.filter(
              (file) => file.claimId === reward.claimId,
            );

            return (
              <div className="border rounded p-3" key={reward.claimId}>
                <div className="d-flex flex-column flex-lg-row align-items-start gap-3">
                  <RewardThumb
                    src={reward.reward.thumb}
                    alt={reward.reward.name}
                    size={72}
                  />

                  <div className="flex-grow-1 w-100">
                    <div className="d-flex flex-column flex-md-row justify-content-between gap-3">
                      <div>
                        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                          <RewardClaimStatusBadge status={reward.status} />
                          <RewardClaimDeliveryTypeBadge
                            deliveryType={reward.deliveryType}
                          />
                          <span className="badge bg-light text-muted border">
                            #{reward.reward.order}
                          </span>
                        </div>
                        <div className="fw-bold text-dark">
                          {reward.reward.name}
                        </div>
                        <div className="text-muted small">
                          {reward.reward.description_short}
                        </div>
                      </div>

                      <div className="d-flex flex-wrap align-items-start justify-content-md-end gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-success"
                          disabled={!requirement?.canRequest}
                          title="Requisitar este prêmio."
                        >
                          <FaPaperPlane className="me-2" />
                          Requisitar
                        </button>
                        <label
                          className="btn btn-sm btn-outline-primary mb-0"
                          title="Enviar arquivos deste prêmio."
                        >
                          <FaUpload className="me-2" />
                          Arquivos
                          <input type="file" className="d-none" multiple />
                        </label>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          title="Abrir conversa sobre este prêmio."
                        >
                          <FaComments className="me-2" />
                          Conversar
                        </button>
                      </div>
                    </div>

                    <div className="d-flex flex-wrap gap-3 border-top mt-2 pt-2">
                      <small className="text-muted">
                        Valor:{" "}
                        <span className="fw-bold text-success">
                          {masks.brlCurrency(Number(reward.reward.price || 0))}
                        </span>
                      </small>
                      <small className="text-muted">
                        Atualizado em{" "}
                        {dayjs(reward.updatedAt).format(
                          "DD/MM/YYYY [às] HH:mm",
                        )}
                      </small>
                      {reward.category && (
                        <small className="text-muted">
                          Categoria: {reward.category.name}
                        </small>
                      )}
                    </div>

                    {requirement && (
                      <div className="alert alert-light border mt-3 mb-0">
                        <div className="fw-semibold mb-1">
                          Solicitação deste prêmio
                        </div>
                        <div className="text-muted small mb-2">
                          {requirement.instructions}
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                          {requirement.requiredFiles.map((file) => (
                            <span
                              className="badge bg-light text-muted border"
                              key={`${reward.claimId}-${file}`}
                            >
                              {file}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {rewardFiles.length > 0 && (
                      <div className="mt-3">
                        <div className="fw-semibold small mb-2">
                          Arquivos deste prêmio
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                          {rewardFiles.map((file) => (
                            <span
                              className="badge bg-light text-muted border"
                              key={file.id}
                              title={file.type}
                            >
                              <FaFileArrowUp className="me-1 text-primary" />
                              {file.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
